import { useCallback, useMemo } from 'react';

import { useMutation, useQuery } from '@apollo/client';

import { TOUR_CONFIGS, TourId } from './config';
import {
  getLocalStorageTourDisplayCount,
  recordLocalStorageTourDisplay,
} from './localStorageTourTracker';
import { TourDisplayBehavior, TourDisplayRecord } from './tourTracker';
import { ADD_USER_SETTING } from '../mutation/userData';
import { GET_USER_SETTING } from '../query/userData';

const TOUR_SETTINGS_KEY = 'tour_display_records';

type TourDisplayRecords = Record<string, TourDisplayRecord>;

export const useTourTracking = () => {
  const { data, loading, refetch } = useQuery(GET_USER_SETTING, {
    variables: { name: TOUR_SETTINGS_KEY },
    fetchPolicy: 'cache-and-network',
    skip: false,
    errorPolicy: 'ignore',
  });

  const [addUserSetting] = useMutation(ADD_USER_SETTING, {
    onCompleted: () => {
      refetch();
    },
  });

  const tourDisplayRecords = useMemo<TourDisplayRecords>(() => {
    if (!data?.user?.setting) {
      return {};
    }

    try {
      return JSON.parse(data.user.setting) as TourDisplayRecords;
    } catch (error) {
      console.warn('Failed to parse tour display records:', error);
      return {};
    }
  }, [data]);

  const getTourDisplayCount = useCallback(
    (tourId: string): TourDisplayRecord | null => {
      const config = TOUR_CONFIGS[tourId as TourId];
      if (config?.useLocalStorage) {
        return getLocalStorageTourDisplayCount(tourId);
      }
      return tourDisplayRecords[tourId] || null;
    },
    [tourDisplayRecords],
  );

  const recordTourDisplay = useCallback(
    async (tourId: string): Promise<void> => {
      const config = TOUR_CONFIGS[tourId as TourId];
      if (config?.useLocalStorage) {
        recordLocalStorageTourDisplay(tourId);
        return;
      }

      try {
        const existing = tourDisplayRecords[tourId];
        const newRecord: TourDisplayRecord = {
          count: (existing?.count || 0) + 1,
          lastShown: new Date().toISOString(),
        };

        const updatedRecords: TourDisplayRecords = {
          ...tourDisplayRecords,
          [tourId]: newRecord,
        };

        await addUserSetting({
          variables: {
            name: TOUR_SETTINGS_KEY,
            value: JSON.stringify(updatedRecords),
          },
        });
      } catch (error) {
        console.warn(`Failed to record tour display for ${tourId}:`, error);
      }
    },
    [tourDisplayRecords, addUserSetting],
  );

  const shouldDisplayTour = useCallback(
    (tourId: string, displayBehavior: TourDisplayBehavior): boolean => {
      const record = getTourDisplayCount(tourId);

      if (record?.doNotShow === true) {
        return false;
      }

      if (displayBehavior === 'always') {
        return true;
      }

      if (displayBehavior === 'once') {
        return record === null || record.count === 0;
      }

      if (typeof displayBehavior === 'number') {
        const currentCount = record?.count || 0;
        return currentCount < displayBehavior;
      }

      return true;
    },
    [getTourDisplayCount],
  );

  const resetTourDisplayCount = useCallback(
    async (tourId: string): Promise<void> => {
      try {
        const updatedRecords: TourDisplayRecords = { ...tourDisplayRecords };
        delete updatedRecords[tourId];

        await addUserSetting({
          variables: {
            name: TOUR_SETTINGS_KEY,
            value: JSON.stringify(updatedRecords),
          },
        });
      } catch (error) {
        console.warn(`Failed to reset tour display count for ${tourId}:`, error);
      }
    },
    [tourDisplayRecords, addUserSetting],
  );

  const dismissTourPermanently = useCallback(
    async (tourId: string): Promise<void> => {
      try {
        const config = TOUR_CONFIGS[tourId as TourId];
        const existing = getTourDisplayCount(tourId);

        const dismissedRecord: TourDisplayRecord = {
          count: existing?.count || 0,
          lastShown: existing?.lastShown || new Date().toISOString(),
          doNotShow: true,
        };

        if (config?.useLocalStorage) {
          recordLocalStorageTourDisplay(tourId, {
            count: existing?.count || 0,
            lastShown: existing?.lastShown || new Date().toISOString(),
            doNotShow: true,
          });
          return;
        }

        const updatedRecords: TourDisplayRecords = {
          ...tourDisplayRecords,
          [tourId]: dismissedRecord,
        };

        await addUserSetting({
          variables: {
            name: TOUR_SETTINGS_KEY,
            value: JSON.stringify(updatedRecords),
          },
        });
      } catch (error) {
        console.warn(`Failed to dismiss tour ${tourId}:`, error);
      }
    },
    [tourDisplayRecords, getTourDisplayCount, addUserSetting],
  );

  return {
    tourDisplayRecords,
    loading,
    getTourDisplayCount,
    recordTourDisplay,
    shouldDisplayTour,
    resetTourDisplayCount,
    dismissTourPermanently,
  };
};
